import { useRef, useMemo, useLayoutEffect } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';

/**
 * Creates handlers to detect when focus leaves a dom node and its children. The onFocusOut
 * callback will be called after onBlur and onFocus events fire.
 *
 * @param onFocusOut called when focus leaves the DOM element the returned handlers are on.
 *
 * @returns Returns an onBlur and onFocus handler that should be attached to a DOM node
 *
 * @see https://reactjs.org/docs/accessibility.html#mouse-and-pointer-events
 * @see https://github.com/facebook/react/issues/6410
 *
 * @example
 * ```
 * function MyComponent(props) {
 *   const { onBlur, onFocus } = useOnFocusOut(() => console.log('focus out fired!'))
 *
 *   return <div onBlur={onBlur} onFocus={onFocus}>
 *     <input />
 *   </div>
 * }
 * ```
 */
export default function useOnFocusOut(onFocusOut: (() => void) | null | undefined) {
  const refCallback = useRef<typeof onFocusOut>(undefined);
  useLayoutEffect(function saveCallback() {
    refCallback.current = onFocusOut;
  });

  return useMemo(() => {
    let timeoutHandle: number | undefined;
    // TODO: Emit warnings if one is bound but not the other?
    return {
      onBlur() {
        if (!isNullish(timeoutHandle)) return;

        timeoutHandle = setTimeout(() => {
          if (refCallback.current) {
            refCallback.current();
          }
        });
      },
      onFocus() {
        if (!isNullish(timeoutHandle)) {
          clearTimeout(timeoutHandle);
          timeoutHandle = undefined;
        }
      },
    };
  }, []);
}
